import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { getRankings } from "@/lib/api";

const STATIC_SECTIONS = [
  {
    title: "Product",
    links: [
      { label: "Home", to: "/" },
      { label: "Analyze Channel", to: "/analyze" },
      { label: "Compare Channels", to: "/compare" },
      { label: "Rankings", to: "/rankings" },
      { label: "Dashboard", to: "/dashboard" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "API Reference", to: "/api" },
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
      { label: "Support", to: "/legal/support" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/legal/privacy" },
      { label: "Terms of Service", to: "/legal/terms" },
      { label: "Cookie Policy", to: "/legal/cookies" },
      { label: "Disclaimer", to: "/legal/disclaimer" },
    ],
  },
];

export default function SiteMapPage() {
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    getRankings("subscribers").then((rows) => setChannels(rows.slice(0, 50))).catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
      <SEO
        title="HTML Sitemap"
        description="Browse the full sitemap of Creator Intelligence Tube. Find every public page: analysis, rankings, channels, and legal documents."
        path="/sitemap"
      />
      <Breadcrumbs items={[{ name: "Sitemap", path: "/sitemap" }]} />
      <div className="overline mt-6">Sitemap</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">All pages, one place</h1>
      <p className="mt-4 text-muted-foreground max-w-2xl">
        Every public page on Creator Intelligence Tube. For machines, we also publish XML sitemaps
        at <code className="font-mono text-primary text-sm">/api/sitemap-index.xml</code>.
      </p>

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {STATIC_SECTIONS.map((s) => (
          <div key={s.title} className="card-flat p-6">
            <div className="overline mb-4">{s.title}</div>
            <ul className="space-y-2.5">
              {s.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm hover:text-primary transition-colors flex items-center gap-1.5 group">
                    <ChevronRight className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {channels.length > 0 && (
        <>
          <h2 className="mt-16 text-2xl font-extrabold tracking-tight">Analyzed channels</h2>
          <p className="mt-2 text-sm text-muted-foreground">Popular channels indexed on the platform.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {channels.map((c) => (
              <Link
                key={c.channel_id}
                to={`/channel/${c.channel_id}`}
                className="text-sm text-muted-foreground hover:text-primary py-1.5 truncate"
                title={c.title}
              >
                <ChevronRight className="w-3 h-3 inline mr-1 opacity-50" />
                {c.title}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
